// src/utils/manuscriptThemeApi.js
//
// Saving manuscript themes — not `src/theme.js`, which is the app's own tokens.
//
// Two stores, on purpose (backend/routes/manuscript_themes.py says why):
//   - the author's own named themes, reusable on any book;
//   - the copy one manuscript uses, on the draft as `manuscriptTheme`.
// Applying a theme writes the copy. Changing a saved theme later never reaches a
// book that already uses it.

import {
  API_BASE, getJSON, postJSON, putJSON, tryRefreshAndRetry, clearSessionAndSignal,
} from './api';
import { themeValues } from './manuscriptTheme';

/** The error code the API answered with, from an api.js helper's thrown Error. */
export function errorCode(err) {
  try {
    return JSON.parse(err?.message || '')?.code || null;
  } catch {
    return null;
  }
}

export const listSavedThemes = () => getJSON('/api/manuscript-themes');

export const createSavedTheme = (name, theme) =>
  postJSON('/api/manuscript-themes', { name, ...themeValues(theme) });

export const updateSavedTheme = (id, name, theme) =>
  putJSON(`/api/manuscript-themes/${id}`, { name, ...themeValues(theme) });

export const renameSavedTheme = (id, name) => putJSON(`/api/manuscript-themes/${id}`, { name });

export async function deleteSavedTheme(id) {
  const url = `${API_BASE}/api/manuscript-themes/${id}`;
  const token = localStorage.getItem('rv_access_token');
  const init = {
    method: 'DELETE',
    credentials: 'omit',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  };
  const res = await fetch(url, init);
  if (res.status === 401) {
    const retry = await tryRefreshAndRetry(url, init);
    if (retry && retry.ok) return retry.json();
    clearSessionAndSignal();
    return null;
  }
  if (!res.ok) throw new Error((await res.text().catch(() => '')) || `DELETE failed: ${res.status}`);
  return res.json();
}

/**
 * Upload an image for page art. Returns `{src, name, w, h}` — `src` goes in the
 * theme's `art.src`. PNG, JPG or WebP up to 15 MB; the server re-encodes it.
 * Errors carry the API's JSON body, as the other helpers' do (see errorCode).
 */
export async function uploadThemeArt(file) {
  const url = `${API_BASE}/api/manuscript-theme-art`;
  const body = new FormData();
  body.append('file', file);
  const token = localStorage.getItem('rv_access_token');
  const init = {
    method: 'POST',
    credentials: 'omit',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body,
  };
  const res = await fetch(url, init);
  if (res.status === 401) {
    const retry = await tryRefreshAndRetry(url, init);
    if (retry && retry.ok) return retry.json();
    clearSessionAndSignal();
    return null;
  }
  if (!res.ok) throw new Error((await res.text().catch(() => '')) || `Upload failed: ${res.status}`);
  return res.json();
}

/**
 * Put a theme on a manuscript (a copy of its values, its name and where it came
 * from), or take it off with `null`. Author only — the API answers 403
 * `not_author` to anyone else.
 */
export const setManuscriptTheme = (draftId, theme, { name, sourceId } = {}) =>
  putJSON(`/api/drafts/${draftId}`, {
    manuscriptTheme: theme === null ? null : { name, sourceId, ...themeValues(theme) },
  });
